/**
 * Shared "nearby" proximity model: who counts as within earshot, and how loud
 * a remote mic should be at a given distance. Consumed by the chat user picker
 * (src/ui/chatUI.js), the Connected Users panel (src/ui/multiplayerUI.js), and
 * the proximity-audio loop (src/entities/AvatarManager.js) - all on the same
 * CONFIG.NEARBY radii, so "in the Nearby list" always means "within voice
 * range".
 *
 * Pure and window-free so it unit-tests like src/services/worldSession.js.
 */

import { CONFIG } from '../config/client.config.js';

/**
 * One entry of the roster snapshot produced by multiplayer.js's
 * getRosterSnapshot(): every other participant with their live distance from
 * the local camera.
 * @typedef {Object} RosterEntry
 * @property {number} actorNr
 * @property {string} name
 * @property {string|null} [role] - meeting role key (e.g. 'host'), if synced
 * @property {boolean} [sameLevel]
 * @property {string} [level]
 * @property {number|null} distance - world units from the local camera; null
 *   when the user is on another level (they can never be "nearby")
 */

/**
 * Create an independent nearby-membership tracker with enter/exit hysteresis:
 * a user enters the set at <= enterRadius and only leaves past > exitRadius,
 * so someone pacing the boundary doesn't flicker in and out between refreshes.
 *
 * @param {Object} [options]
 * @param {number} [options.enterRadius] - defaults to CONFIG.NEARBY.RADIUS
 * @param {number} [options.exitRadius] - defaults to CONFIG.NEARBY.EXIT_RADIUS
 */
export function createNearbyTracker({
  enterRadius = CONFIG.NEARBY.RADIUS,
  exitRadius = CONFIG.NEARBY.EXIT_RADIUS,
} = {}) {
  /** @type {Set<number>} actorNrs currently inside the hysteresis band */
  const nearbySet = new Set();

  return {
    /**
     * Apply hysteresis against a fresh roster snapshot.
     * @param {RosterEntry[]} roster
     * @returns {RosterEntry[]} the nearby entries, sorted by distance ascending
     */
    update(roster) {
      const present = new Set(roster.map((u) => u.actorNr));
      for (const nr of nearbySet) {
        if (!present.has(nr)) nearbySet.delete(nr);
      }
      for (const user of roster) {
        if (user.distance === null) {
          nearbySet.delete(user.actorNr);
        } else if (nearbySet.has(user.actorNr)) {
          if (user.distance > exitRadius) nearbySet.delete(user.actorNr);
        } else if (user.distance <= enterRadius) {
          nearbySet.add(user.actorNr);
        }
      }
      return roster
        .filter((u) => nearbySet.has(u.actorNr))
        .sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
    },

    /**
     * @param {number} actorNr
     * @returns {boolean} whether the actor is currently in the nearby set
     */
    has(actorNr) {
      return nearbySet.has(actorNr);
    },

    /** Forget all membership (room change / chat reset). */
    clear() {
      nearbySet.clear();
    },
  };
}

/**
 * Subscriber-side mic volume (0-100) for a remote user at `distance`: full
 * volume inside fullRadius, linear fade to silence at exitRadius, silent
 * beyond it or on another level (distance null). Silence deliberately
 * coincides with Nearby-list exit, so "in the list" always means "audible".
 *
 * @param {number|null|undefined} distance - world units; null = another level
 * @param {Object} [options]
 * @param {number} [options.fullRadius] - defaults to CONFIG.NEARBY.AUDIO_FULL_RADIUS
 * @param {number} [options.exitRadius] - defaults to CONFIG.NEARBY.EXIT_RADIUS
 * @returns {number} integer volume in [0, 100]
 */
export function volumeForDistance(
  distance,
  { fullRadius = CONFIG.NEARBY.AUDIO_FULL_RADIUS, exitRadius = CONFIG.NEARBY.EXIT_RADIUS } = {},
) {
  if (typeof distance !== 'number' || Number.isNaN(distance)) return 0;
  if (distance <= fullRadius) return 100;
  if (distance >= exitRadius) return 0;
  return Math.round((100 * (exitRadius - distance)) / (exitRadius - fullRadius));
}
